import { useEffect, useState } from "react";
import { API } from "../api";

const STORAGE_KEY = "real_exam_data";

export default function RealExamPage() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);

  // =========================
  //  โหลดข้อสอบ
  // =========================
  const loadExam = async () => {
    setLoading(true);
    try {
      const res = await API.get("/exam/random/?count=200");

      setQuestions(res.data);
      setAnswers({});
      setShowResult(false);
      setScore(0);

      //  save ใหม่ทันที
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          questions: res.data,
          answers: {},
          showResult: false,
          score: 0,
        })
      );
    } catch (err) {
      console.error(err);
      alert("โหลดข้อสอบไม่สำเร็จ");
    }
    setLoading(false);
  };

  // =========================
  //  Resume จาก localStorage
  // =========================
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);

    if (saved) {
      const data = JSON.parse(saved);

      setQuestions(data.questions || []);
      setAnswers(data.answers || {});
      setShowResult(data.showResult || false);
      setScore(data.score || 0);
      setLoading(false);
    } else {
      loadExam();
    }
  }, []);

  // =========================
  //  Auto Save
  // =========================
  useEffect(() => {
    if (questions.length === 0) return;

    const data = {
      questions,
      answers,
      showResult,
      score,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [questions, answers, showResult, score]);

  // =========================
  //  เลือกคำตอบ
  // =========================
  const handleSelect = (qId, choice) => {
    if (showResult) return;

    setAnswers((prev) => ({
      ...prev,
      [qId]: choice,
    }));
  };

  // =========================
  // ✅ Submit
  // =========================
  const handleSubmit = () => {
    let correct = 0;

    questions.forEach((q) => {
      if (answers[q.id] === q.correct_answer) {
        correct++;
      }
    });

    setScore(correct);
    setShowResult(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================
  //  Restart (ล้าง + โหลดใหม่)
  // =========================
  const handleRestart = async () => {
    const confirm = window.confirm("ต้องการเริ่มทำข้อสอบใหม่หรือไม่?");
    if (!confirm) return;

    localStorage.removeItem(STORAGE_KEY); //  ล้างของเก่า

    await loadExam();

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================
  // ⏳ Loading
  // =========================
  if (loading) {
    return (
      <div className="text-center mt-20 text-xl font-semibold">
        ⏳ กำลังโหลดข้อสอบ...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-6">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <h1 className="text-3xl font-bold text-white mb-6 text-center">
           Real Exam (200 ข้อ)
        </h1>

        {/*  Restart */}
        <div className="text-center mb-4">
          <button
            onClick={handleRestart}
            className="px-6 py-2 bg-yellow-400 text-black font-semibold rounded-lg shadow hover:opacity-90"
          >
             ทำข้อสอบใหม่
          </button>
        </div>

        {/* Result */}
        {showResult && (
          <div className="bg-white p-6 rounded-xl shadow mb-6 text-center">
            <h2 className="text-2xl font-bold mb-2">
               ผลคะแนนของคุณ
            </h2>
            <p className="text-xl">
              {score} / {questions.length} คะแนน
            </p>
          </div>
        )}

        {/* Questions */}
        {questions.map((q, index) => (
          <div
            key={q.id}
            className="bg-white p-6 mb-6 rounded-xl shadow"
          >
            <div className="text-sm text-indigo-500 mb-2 font-semibold">
               {q.subject || "ทั่วไป"}
            </div>

            <p className="font-semibold mb-4">
              {index + 1}. {q.question_text}
            </p>

            <div className="space-y-2">
              {q.choices.map((choice, i) => {
                const isSelected = answers[q.id] === choice.key;
                const isCorrect = choice.key === q.correct_answer;

                return (
                  <div
                    key={i}
                    onClick={() => handleSelect(q.id, choice.key)}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition
                      ${
                        isSelected
                          ? "bg-indigo-500 text-white border-indigo-500"
                          : "bg-gray-50 hover:bg-indigo-100"
                      }
                      ${
                        showResult && isCorrect
                          ? "border-green-500 bg-green-100 text-black"
                          : ""
                      }
                      ${
                        showResult &&
                        isSelected &&
                        !isCorrect
                          ? "border-red-500 bg-red-100 text-black"
                          : ""
                      }
                    `}
                  >
                    {choice.text}
                  </div>
                );
              })}
            </div>

            {showResult && (
              <div className="mt-4 text-sm text-gray-600">
                 {q.explanation || "ไม่มีคำอธิบาย"}
              </div>
            )}
          </div>
        ))}

        {/* Submit */}
        {!showResult && (
          <div className="text-center mt-10">
            <button
              onClick={handleSubmit}
              className="px-10 py-3 bg-gradient-to-r from-indigo-500 to-pink-500 text-white font-semibold rounded-xl shadow hover:opacity-90"
            >
              ✅ Submit Exam
            </button>
          </div>
        )}
      </div>
    </div>
  );
}